import { createBrowserRouter, Outlet, type RouteObject } from "react-router";
import { AdminLayout } from "@/features/admin/AdminLayout";
import { adminRoutes } from "@/features/admin/adminRoutes";
import { AgentDetailPage } from "@/features/build/agents/AgentDetailPage";
import { AgentsListPage } from "@/features/build/agents/AgentsListPage";
import { NewAgentPage } from "@/features/build/agents/NewAgentPage";
import { BuildHomePage } from "@/features/build/BuildHomePage";
import { BuildWithAiPage } from "@/features/build/BuildWithAiPage";
import { BrandDetailPage } from "@/features/build/brands/BrandDetailPage";
import { BrandsListPage } from "@/features/build/brands/BrandsListPage";
import { NewBrandPage } from "@/features/build/brands/NewBrandPage";
import { ConnectorDetailPage } from "@/features/build/connectors/ConnectorDetailPage";
import { ConnectorsListPage } from "@/features/build/connectors/ConnectorsListPage";
import { NewConnectorPage } from "@/features/build/connectors/NewConnectorPage";
import { DataSourceDetailPage } from "@/features/build/dataSources/DataSourceDetailPage";
import { DataSourcesListPage } from "@/features/build/dataSources/DataSourcesListPage";
import { NewDataSourcePage } from "@/features/build/dataSources/NewDataSourcePage";
import { MicroAppDetailPage } from "@/features/build/microApps/MicroAppDetailPage";
import { MicroAppsListPage } from "@/features/build/microApps/MicroAppsListPage";
import { MicroAppViewerPage } from "@/features/build/microApps/MicroAppViewerPage";
import { NewMicroAppPage } from "@/features/build/microApps/NewMicroAppPage";
import { ImportSkillsPage } from "@/features/build/skills/ImportSkillsPage";
import { NewSkillPage } from "@/features/build/skills/NewSkillPage";
import { SkillDetailPage } from "@/features/build/skills/SkillDetailPage";
import { SkillsListPage } from "@/features/build/skills/SkillsListPage";
import { BuildHistoryPage } from "@/features/build/withAi/BuildHistoryPage";
import { FlowDetailPage } from "@/features/build/workflows/FlowDetailPage";
import { FlowRunDetailPage } from "@/features/build/workflows/FlowRunDetailPage";
import { FlowsListPage } from "@/features/build/workflows/FlowsListPage";
import { NewFlowPage } from "@/features/build/workflows/NewFlowPage";
import { ChatPage } from "@/features/chat/ChatPage";
import { DeepResearchSharedLinkRedirect } from "@/features/chat/DeepResearchSharedLinkRedirect";
import { ChatsListPage } from "@/features/chat/history/ChatsListPage";
import { SharedChatLinkPage } from "@/features/chat/SharedChatLinkPage";
import { NotFoundPage } from "@/features/errors/NotFoundPage";
import { RouteErrorPage } from "@/features/errors/RouteErrorPage";
import { HelpArticlePage } from "@/features/help/HelpArticlePage";
import { HelpLandingPage } from "@/features/help/HelpLandingPage";
import { HelpLayout } from "@/features/help/HelpLayout";
import { HomePage } from "@/features/home/HomePage";
import { DuplicatesPage } from "@/features/knowledge/duplicates/DuplicatesPage";
import { InboxPage } from "@/features/knowledge/inbox/InboxPage";
import { KnowledgePage } from "@/features/knowledge/KnowledgePage";
import { RequireKnowledgeGraph, SpaceWikiRoute } from "@/features/knowledge/RequireKnowledgeGraph";
import { SettingsLayout } from "@/features/settings/SettingsLayout";
import { settingsRoutes } from "@/features/settings/settingsRoutes";
import { AppLayout } from "@/features/shell/AppLayout";
import { SpaceAppPage } from "@/features/spaces/detail/SpaceAppPage";
import { SpaceChatsPage } from "@/features/spaces/detail/SpaceChatsPage";
import { SpaceDetailPage } from "@/features/spaces/detail/SpaceDetailPage";
import { SpaceKnowledgePage } from "@/features/spaces/detail/SpaceKnowledgePage";
import { SpaceWorkflowPage, SpaceWorkflowRunPage } from "@/features/spaces/detail/SpaceWorkflowPage";
import { SpacesPage } from "@/features/spaces/SpacesPage";
import { AgentsSection as SpaceAgentsSection } from "@/features/spaces/settings/AgentsSection";
import { AppsSection as SpaceAppsSection } from "@/features/spaces/settings/AppsSection";
import { SpaceConnectorsSection } from "@/features/spaces/settings/connectors/SpaceConnectorsSection";
import { GeneralSection } from "@/features/spaces/settings/GeneralSection";
import { KnowledgeSection } from "@/features/spaces/settings/KnowledgeSection";
import { MembersSection } from "@/features/spaces/settings/MembersSection";
import { QuickActionsSection } from "@/features/spaces/settings/QuickActionsSection";
import { SpaceSettingsLayout } from "@/features/spaces/settings/SpaceSettingsLayout";
import { WorkflowsSection as SpaceWorkflowsSection } from "@/features/spaces/settings/WorkflowsSection";
import { DESKTOP_ONLY, OnSurfaces } from "@/responsive/surfaces";

// Build is desktop-only (see docs/design/web/WEB_BUILD_PATTERN_PLAN.md); surface
// availability lives here so the mobile feature set is reviewable in one file.
const buildRoutes: RouteObject[] = [
  { index: true, element: <BuildHomePage /> },
  { path: "agents", element: <AgentsListPage /> },
  { path: "agents/new", element: <NewAgentPage /> },
  { path: "agents/new/:mode", element: <NewAgentPage /> },
  { path: "agents/:uuid", element: <AgentDetailPage /> },
  { path: "agents/:uuid/:tab", element: <AgentDetailPage /> },
  { path: "skills", element: <SkillsListPage /> },
  { path: "skills/new", element: <NewSkillPage /> },
  { path: "skills/import", element: <ImportSkillsPage /> },
  { path: "skills/:uuid", element: <SkillDetailPage /> },
  { path: "skills/:uuid/:tab", element: <SkillDetailPage /> },
  { path: "connectors", element: <ConnectorsListPage /> },
  { path: "connectors/new", element: <NewConnectorPage /> },
  { path: "connectors/:uuid", element: <ConnectorDetailPage /> },
  { path: "connectors/:uuid/:tab", element: <ConnectorDetailPage /> },
  { path: "data-sources", element: <DataSourcesListPage /> },
  { path: "data-sources/new", element: <NewDataSourcePage /> },
  { path: "data-sources/new/:type", element: <NewDataSourcePage /> },
  { path: "data-sources/:uuid", element: <DataSourceDetailPage /> },
  { path: "data-sources/:uuid/:tab", element: <DataSourceDetailPage /> },
  { path: "brands", element: <BrandsListPage /> },
  { path: "brands/new", element: <NewBrandPage /> },
  { path: "brands/:uuid", element: <BrandDetailPage /> },
  { path: "brands/:uuid/:tab", element: <BrandDetailPage /> },
  { path: "workflows", element: <FlowsListPage /> },
  { path: "workflows/new", element: <NewFlowPage /> },
  { path: "workflows/:uuid", element: <FlowDetailPage /> },
  { path: "workflows/:uuid/:tab", element: <FlowDetailPage /> },
  { path: "workflows/:uuid/runs/:runUuid", element: <FlowRunDetailPage /> },
  { path: "apps", element: <MicroAppsListPage /> },
  { path: "apps/new", element: <NewMicroAppPage /> },
  { path: "apps/:uuid", element: <MicroAppDetailPage /> },
  { path: "apps/:uuid/view", element: <MicroAppViewerPage /> },
  { path: "apps/:uuid/:tab", element: <MicroAppDetailPage /> },
];

export const routes: RouteObject[] = [
  {
    element: <AppLayout />,
    errorElement: <RouteErrorPage />,
    children: [
      { index: true, element: <HomePage /> },
      { path: "settings", element: <SettingsLayout />, children: settingsRoutes },
      {
        path: "help",
        element: <HelpLayout />,
        children: [
          { index: true, element: <HelpLandingPage /> },
          { path: ":slug", element: <HelpArticlePage /> },
        ],
      },
      { path: "chats", element: <ChatsListPage /> },
      { path: "chat/shared/:token", element: <SharedChatLinkPage /> },
      // Old emails still carry v1 Deep Research links; the report is a chat now (MHS-790).
      { path: "deep-research/shared/:token", element: <DeepResearchSharedLinkRedirect /> },
      { path: "chat/:sessionUuid?", element: <ChatPage /> },
      // Build with AI is a chat, so it works on every surface, unlike the rest of Build.
      { path: "build/with-ai", element: <BuildWithAiPage /> },
      { path: "build/with-ai/history", element: <BuildHistoryPage /> },
      { path: "build/with-ai/:sessionUuid", element: <BuildWithAiPage /> },
      { path: "spaces", element: <SpacesPage /> },
      { path: "spaces/:spaceUuid", element: <SpaceDetailPage /> },
      { path: "spaces/:spaceUuid/chats", element: <SpaceChatsPage /> },
      // A micro-app opened from its space: the Build viewer, kept under the space and on every surface.
      { path: "spaces/:spaceUuid/apps/:uuid", element: <SpaceAppPage /> },
      // A workflow used from its space: its runs and Run, under the space (building it stays in Build).
      { path: "spaces/:spaceUuid/workflows/:uuid", element: <SpaceWorkflowPage /> },
      { path: "spaces/:spaceUuid/workflows/:uuid/runs/:runUuid", element: <SpaceWorkflowRunPage /> },
      // Knowledge: the space's wiki (knowledge graph), and its uploaded files.
      {
        path: "spaces/:spaceUuid/knowledge",
        element: (
          <SpaceWikiRoute>
            <KnowledgePage />
          </SpaceWikiRoute>
        ),
      },
      { path: "spaces/:spaceUuid/knowledge/files", element: <SpaceKnowledgePage /> },
      // Possible duplicate pages and merges (MHS-756), for the space's editors.
      {
        path: "spaces/:spaceUuid/knowledge/duplicates",
        element: (
          <RequireKnowledgeGraph>
            <DuplicatesPage />
          </RequireKnowledgeGraph>
        ),
      },
      // Facts suggested from your own chats (MHS-755); not tied to one space.
      {
        path: "knowledge/inbox",
        element: (
          <RequireKnowledgeGraph>
            <InboxPage />
          </RequireKnowledgeGraph>
        ),
      },
      {
        path: "spaces/:spaceUuid/settings",
        element: <SpaceSettingsLayout />,
        children: [
          { path: "general", element: <GeneralSection /> },
          { path: "members", element: <MembersSection /> },
          { path: "agents", element: <SpaceAgentsSection /> },
          { path: "knowledge", element: <KnowledgeSection /> },
          { path: "apps", element: <SpaceAppsSection /> },
          { path: "workflows", element: <SpaceWorkflowsSection /> },
          { path: "quick-actions", element: <QuickActionsSection /> },
          { path: "connectors", element: <SpaceConnectorsSection /> },
        ],
      },
      {
        path: "build",
        element: (
          <OnSurfaces only={DESKTOP_ONLY}>
            <Outlet />
          </OnSurfaces>
        ),
        children: buildRoutes,
      },
      {
        path: "admin",
        element: <AdminLayout />,
        children: adminRoutes,
      },
      { path: "*", element: <NotFoundPage /> },
    ],
  },
];

export const createRouter = () => createBrowserRouter(routes);
